<template>
  <div class="rutinas-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="rutinas-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-clipboard-list-outline</v-icon>
          </div>
          <div>
            <h1 class="page-title">Rutinas de entrenamiento</h1>
            <p class="page-sub">Gestiona y asigna rutinas personalizadas</p>
          </div>
        </div>
        <button class="btn-primary" @click="$router.push('/rutinas/crear')">
          <v-icon size="15" class="mr-2">mdi-plus</v-icon>
          Crear nueva rutina
        </button>
      </header>

      <!-- Stats -->
      <section class="dash-section fade-in delay-100">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon stat-icon-accent">
                <v-icon size="18" color="#0E0E10">mdi-format-list-bulleted</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ rutinas.length }}</div>
            <div class="stat-label">Total rutinas</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-account-check</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ rutinasPrincipiante }}</div>
            <div class="stat-label">Principiante</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-account-star</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ rutinasIntermedio }}</div>
            <div class="stat-label">Intermedio</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-fire</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ rutinasAvanzado }}</div>
            <div class="stat-label">Avanzado</div>
          </div>
        </div>
      </section>

      <!-- Card principal -->
      <section class="dash-section fade-in delay-200">
        <div class="card-flat card-flat-nopad">
          <div class="table-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Listado de rutinas
            </span>
          </div>

          <div class="table-controls">
            <div class="search-input-wrapper">
              <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
              <input v-model="search" type="text" class="search-input" placeholder="Buscar rutinas…" />
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="rutinas"
            :search="search"
            :loading="cargando"
            loading-text="Cargando rutinas…"
            sort-by="nombre"
            no-data-text="No hay rutinas creadas"
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.nombre`]="{ item }">
              <span class="cell-strong">{{ item.nombre }}</span>
            </template>

            <template v-slot:[`item.objetivo`]="{ item }">
              <span class="status-tag status-tag-neutral">{{ item.objetivo.replace('_', ' ') }}</span>
            </template>

            <template v-slot:[`item.nivel_dificultad`]="{ item }">
              <span class="nivel-badge" :class="`nivel-${item.nivel_dificultad}`">
                {{ item.nivel_dificultad }}
              </span>
            </template>

            <template v-slot:[`item.duracion_semanas`]="{ item }">
              <span class="cell-secondary">{{ item.duracion_semanas }} semanas</span>
            </template>

            <template v-slot:[`item.total_dias`]="{ item }">
              <span class="status-tag status-tag-success">{{ item.total_dias }} días</span>
            </template>

            <template v-slot:[`item.acciones`]="{ item }">
              <div class="flex gap-xs">
                <button class="icon-btn" @click="verRutina(item)" title="Ver detalles">
                  <v-icon size="15" color="#8C8C93">mdi-eye</v-icon>
                </button>
                <button class="icon-btn" @click="editarRutina(item)" title="Editar">
                  <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                </button>
                <button class="icon-btn icon-btn-accent" @click="asignarRutina(item)" title="Asignar">
                  <v-icon size="15" color="#C8FF4D">mdi-account-plus</v-icon>
                </button>
                <button class="icon-btn icon-btn-danger" @click="eliminarRutina(item)" title="Eliminar">
                  <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                </button>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-clipboard-outline</v-icon>
                </div>
                <h3 class="empty-title">No hay rutinas creadas</h3>
                <p class="empty-desc">Comienza creando tu primera rutina de entrenamiento</p>
                <button class="btn-primary mt-lg" @click="$router.push('/rutinas/crear')">
                  <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                  Crear primera rutina
                </button>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando rutinas…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </div>

    <!-- Diálogo de detalles -->
    <v-dialog v-model="dialogoDetalles" max-width="760px">
      <div class="modal-panel" v-if="rutinaSeleccionada">
        <div class="modal-head">
          <h3 class="modal-title">{{ rutinaSeleccionada.nombre }}</h3>
          <span class="nivel-badge mt-sm" :class="`nivel-${rutinaSeleccionada.nivel_dificultad}`">
            {{ rutinaSeleccionada.nivel_dificultad }}
          </span>
        </div>

        <div class="modal-body">
          <div class="info-table mb-lg">
            <div class="info-row">
              <span class="info-key">Objetivo</span>
              <span class="info-val">{{ rutinaSeleccionada.objetivo }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Duración</span>
              <span class="info-val">{{ rutinaSeleccionada.duracion_semanas }} semanas</span>
            </div>
            <div class="info-row">
              <span class="info-key">Días por semana</span>
              <span class="info-val">{{ rutinaSeleccionada.total_dias }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Descripción</span>
              <span class="info-val">{{ rutinaSeleccionada.descripcion || 'Sin descripción' }}</span>
            </div>
          </div>

          <span class="mini-eyebrow">Días de entrenamiento</span>
          <v-expansion-panels class="dark-expansion mt-sm">
            <v-expansion-panel v-for="dia in rutinaSeleccionada.dias" :key="dia.id">
              <v-expansion-panel-header>
                <span class="expansion-title">{{ dia.nombre_dia }}</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="dia.ejercicios && dia.ejercicios.length > 0" class="table-wrapper">
                  <table class="plain-table">
                    <thead>
                      <tr>
                        <th>Ejercicio</th>
                        <th>Series</th>
                        <th>Repeticiones</th>
                        <th>Descanso</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="ejercicio in dia.ejercicios" :key="ejercicio.id">
                        <td class="cell-strong">{{ ejercicio.ejercicio_nombre }}</td>
                        <td class="cell-secondary">{{ ejercicio.series }}</td>
                        <td class="cell-secondary">{{ ejercicio.repeticiones }}</td>
                        <td class="cell-secondary">{{ ejercicio.descanso_segundos }}s</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-inline">No hay ejercicios para este día</div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogoDetalles = false">Cerrar</button>
          <button class="btn-warning-solid" @click="editarRutina(rutinaSeleccionada)">
            <v-icon size="15" class="mr-2">mdi-pencil</v-icon>
            Editar
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Diálogo de eliminación -->
    <v-dialog v-model="dialogoEliminar" max-width="480px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">¿Eliminar rutina?</h3>
        </div>

        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de que quieres eliminar la rutina
            <strong class="text-error">"{{ rutinaAEliminar.nombre }}"</strong>?
          </p>
          <div class="alert-box">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">Esta acción no se puede deshacer.</div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogoEliminar = false">Cancelar</button>
          <button class="btn-danger-solid" @click="confirmarEliminar" :disabled="eliminando">
            <span v-if="!eliminando">Eliminar</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Eliminando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Loading Overlay -->
    <div v-if="cargando && rutinas.length === 0" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando rutinas…</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'

export default {
  name: "ListaRutinas",

  data: () => ({
    cargando: false,
    eliminando: false,
    search: '',
    rutinas: [],
    rutinaSeleccionada: null,
    rutinaAEliminar: {},
    dialogoDetalles: false,
    dialogoEliminar: false,
    mostrarMensaje: false,
    mensaje: { texto: '', color: '' },
    headers: [
      { text: 'Nombre', value: 'nombre' },
      { text: 'Objetivo', value: 'objetivo' },
      { text: 'Nivel', value: 'nivel_dificultad' },
      { text: 'Duración', value: 'duracion_semanas' },
      { text: 'Días/Semana', value: 'total_dias' },
      { text: 'Acciones', value: 'acciones', sortable: false, align: 'center' }
    ]
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        default: return 'mdi-information'
      }
    },

    rutinasPrincipiante() {
      return this.rutinas.filter(r => r.nivel_dificultad === 'principiante').length
    },

    rutinasIntermedio() {
      return this.rutinas.filter(r => r.nivel_dificultad === 'intermedio').length
    },

    rutinasAvanzado() {
      return this.rutinas.filter(r => r.nivel_dificultad === 'avanzado').length
    }
  },

  mounted() {
    this.obtenerRutinas()
  },

  methods: {
    async obtenerRutinas() {
      this.cargando = true
      let payload = { metodo: "get" }

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "rutinas.php")
        this.rutinas = respuesta
      } catch (error) {
        this.mostrarMensaje = true
        this.mensaje = {
          texto: "❌ Error al cargar las rutinas",
          color: "error"
        }
        console.error("Error:", error)
      } finally {
        this.cargando = false
      }
    },

    async verRutina(rutina) {
      this.cargando = true
      let payload = { metodo: "get_by_id", id: rutina.id }

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "rutinas.php")
        this.rutinaSeleccionada = respuesta
        this.dialogoDetalles = true
      } catch (error) {
        this.mostrarMensaje = true
        this.mensaje = {
          texto: "❌ Error al cargar los detalles",
          color: "error"
        }
        console.error("Error:", error)
      } finally {
        this.cargando = false
      }
    },

    editarRutina(rutina) {
      this.$router.push(`/rutinas/editar/${rutina.id}`)
    },

    asignarRutina(rutina) {
      this.$router.push(`/asignar-rutina`)
    },

    eliminarRutina(rutina) {
      this.rutinaAEliminar = rutina
      this.dialogoEliminar = true
    },

    async confirmarEliminar() {
      this.eliminando = true
      let payload = { metodo: "delete", id: this.rutinaAEliminar.id }

      try {
        const eliminado = await HttpService.eliminar("rutinas.php", payload)

        if (eliminado) {
          this.dialogoEliminar = false
          this.mostrarMensaje = true
          this.mensaje = {
            texto: "✅ Rutina eliminada correctamente",
            color: "success"
          }
          await this.obtenerRutinas()
        }
      } catch (error) {
        this.mostrarMensaje = true
        this.mensaje = {
          texto: "❌ Error al eliminar la rutina",
          color: "error"
        }
        console.error("Error:", error)
      } finally {
        this.eliminando = false
      }
    },

    getColorDificultad(nivel) {
      switch (nivel) {
        case 'principiante': return 'green'
        case 'intermedio': return 'orange'
        case 'avanzado': return 'red'
        default: return 'grey'
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.rutinas-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.rutinas-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.rutinas-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== SECTIONS ===== */
.dash-section {
  margin-bottom: 20px;
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

/* ===== STATS ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}

.stat-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px;
}

.stat-top {
  margin-bottom: 14px;
}

.stat-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon-accent {
  background: var(--accent);
  border-color: var(--accent);
}

.stat-value {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.table-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.table-controls {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  max-width: 380px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

/* ===== TABLE CELLS ===== */
.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  white-space: nowrap;
  text-transform: capitalize;
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.nivel-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.nivel-principiante {
  background: var(--accent-dim);
  color: var(--accent);
}

.nivel-intermedio {
  background: rgba(255, 184, 77, 0.12);
  color: var(--warning);
}

.nivel-avanzado {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-accent:hover {
  border-color: rgba(200, 255, 77, 0.3);
  background: var(--accent-dim);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.empty-inline {
  padding: 20px;
  text-align: center;
  font-size: 12.5px;
  color: var(--text-tertiary);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-warning-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--warning);
  color: #0E0E10;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-warning-solid:hover {
  filter: brightness(1.08);
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #0E0E10;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-danger-solid:disabled {
  opacity: 0.6;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0 0 18px;
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== INFO TABLE ===== */
.info-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-key {
  font-size: 12px;
  color: var(--text-tertiary);
  flex-shrink: 0;
}

.info-val {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
}

.mini-eyebrow {
  display: block;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

/* ===== EXPANSION PANELS ===== */
.dark-expansion {
  background: transparent !important;
}

.dark-expansion >>> .v-expansion-panel {
  background: var(--bg) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: var(--radius) !important;
  margin-bottom: 8px;
}

.dark-expansion >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.dark-expansion >>> .v-expansion-panel-header {
  padding: 12px 16px !important;
  min-height: 44px !important;
}

.dark-expansion >>> .v-expansion-panel-header .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-expansion >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px !important;
}

.expansion-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ===== DETAIL TABLE ===== */
.table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.plain-table {
  width: 100%;
  border-collapse: collapse;
}

.plain-table th {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
  padding: 8px 12px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table tr:last-child td {
  border-bottom: none;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

/* ===== Overrides v-data-table ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 22px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 22px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 22px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .rutinas-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .search-input-wrapper {
    max-width: none;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Planes/ListaRutinas.vue